import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../../../../environments/environment';
import { ODataService } from '../../../../../../core/services/odata.service';

export type ElasticSyncStatus = 'pending' | 'active' | 'failed' | 'deleting' | null;

/** Prefer the live row when async delete leaves a stale `deleting` ghost behind. */
export function pickLiveIngestorRecord(
  records: IngestorRecordDetail[] | undefined | null
): IngestorRecordDetail | undefined {
  if (!records?.length) {
    return undefined;
  }

  const live = records.filter((record) => record.elastic_sync_status !== 'deleting').sort((a, b) => b.id - a.id);

  if (live.length) {
    return live[0];
  }

  return [...records].sort((a, b) => b.id - a.id)[0];
}

/** DRF returns `detail`; legacy endpoints sometimes use `message`. */
export function formatIngestorApiError(error: unknown, fallback: string): string {
  const err = error as { error?: unknown; message?: string };
  const body = err?.error;

  if (typeof body === 'string' && body.trim()) {
    return body;
  }

  if (body && typeof body === 'object') {
    const payload = body as { detail?: unknown; message?: unknown };
    if (typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail;
    }
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message;
    }

    const fieldMessages = Object.entries(payload)
      .filter(([key]) => key !== 'detail' && key !== 'message')
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join(', ')}`;
        }
        return `${key}: ${String(value)}`;
      });

    if (fieldMessages.length) {
      return fieldMessages.join('; ');
    }
  }

  return err?.message || fallback;
}

@Injectable({ providedIn: 'root' })
export class IngestorService extends ODataService<IngestorRecord> {
  constructor() {
    const url = `${environment.apiUrl}/api/v1/ingestor/ingestor/`;
    super(inject(HttpClient), url);

    this.state = {
      skip: 0,
      take: 25,
      sort: [{ field: 'created_at', dir: 'desc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };
  }

  public setOrganization(pk: any) {
    this.url = `${environment.apiUrl}/api/v1/ingestor/ingestor/record(${pk})/`;
  }

  public getIngestorAccountMap(pk: any) {
    const url = `${environment.apiUrl}/api/v1/ingestor/ingestor/mapaccounts(${pk})/`;
    return this.http.get<IngestorAccountMapResponse>(url).pipe(map((response) => response));
  }

  public getIngestorRecordsByOrganization(pk: any, ingestorId: number) {
    const url = `${environment.apiUrl}/api/v1/ingestor/ingestor/record(${pk})/`;
    const params = new HttpParams().set('$filter', `ingestor eq ${ingestorId}`);
    return this.http.get<IngestorRecordResponse>(url, { params });
  }

  public addIngestorRecord(record: IngestorRecord) {
    const url = `${environment.apiUrl}/api/v1/ingestor/ingestor/record/`;
    return this.http.post<IngestorRecordResponse>(url, record);
  }

  public addMapAccount(mapAccount: MapAccount) {
    const url = `${environment.apiUrl}/api/v1/ingestor/ingestor/mapaccounts/`;
    return this.http.post<MapAccount>(url, mapAccount);
  }

  public testConnection(testConnectionRequest: TestConnectionRequest) {
    const url = `${environment.apiUrl}/v1/ingestor/ingestor/record/test_connection/`;
    return this.http.post(url, testConnectionRequest);
  }

  public createIngestorRecord(saveRequest: CreateIngestorRecordRequest) {
    const url = `${environment.apiUrl}/v1/ingestor/ingestor/record/`;
    return this.http.post<CreateIngestorRecordResponse>(url, saveRequest);
  }

  public deleteIngestorRecord(recordId: number): Observable<HttpResponse<{ detail?: string }>> {
    const url = `${environment.apiUrl}/v1/ingestor/ingestor/record(${recordId})/`;
    return this.http.delete<{ detail?: string }>(url, { observe: 'response' });
  }

  /** Re-save credentials without dropping the record. Tenant maps hang off the record, so they survive. */
  public updateIngestorRecord(recordId: number, body: UpdateIngestorRecordRequest): Observable<IngestorRecordDetail> {
    const url = `${environment.apiUrl}/v1/ingestor/ingestor/record(${recordId})/`;
    return this.http.put<IngestorRecordDetail>(url, body);
  }

  public resyncElastic(recordId: number) {
    const url = `${environment.apiUrl}/v1/ingestor/ingestor/record(${recordId})/resync_elastic/`;
    return this.http.post<IngestorRecordDetail>(url, {});
  }

  public getIngestorRecordsByOrganizationv2(organizationId: number, ingestorIntegrationId: number) {
    const url = `${environment.apiUrl}/v1/ingestor/ingestor/organizationrecords(${organizationId})/`;
    const params = new HttpParams().set('$filter', `ingestor_integration__id eq ${ingestorIntegrationId}`);
    return this.http
      .get<IngestorRecordDetailResponse>(url, { params })
      .pipe(map((response) => pickLiveIngestorRecord(response.value)));
  }
}

export class IngestorRecord {
  id?: number | null = null;
  ingestor?: number | null = null;
  integration_url?: string | null = null;
  organization_parent?: number | null = null;
  api_key?: string | null = null;
  mapped_customers?: number | null = null;
  created_by?: string | null = null;
  created_at?: string | null = null;
  last_sync?: string | null = null;
}

export interface IngestorAccountMap {
  pk: number | null;
  ingestorrecord: number | null;
  organization_id: number | null;
  integration_unique_name: string | null;
  integration_id: number | null;
  active: boolean | null;
  edited: boolean | null;
}

export interface OrgNotAssigned {
  pk: number | null;
  name: string | null;
}

export interface IngestorAccountMapResponse {
  values: IngestorAccountMap[];
  orgs_not_assigned: OrgNotAssigned[];
}

export interface IngestorRecordResponse {
  values: IngestorRecord[];
}

export class MapAccount {
  integration_unique_name: string | null = null;
  integration_url: string | null = null;
  api_key: string | null = null;
}

export interface IntegrationCredentials {
  apiUrl?: { value: string };
  authUrl?: { value: string };
  clientId?: string;
  tenantId?: string;
  clientSecret?: string;
}

export interface TestConnectionRequest {
  ingestor_integration: number;
  organization: number;
  integration_credentials: IntegrationCredentials;
}

export interface CreateIngestorRecordRequest {
  ingestor_integration: number;
  organization: number;
  configuration_status: number;
  integration_credentials: IntegrationCredentials;
  is_multitenant: boolean;
}

/** Body of PUT record({id})/. ingestor_integration and organization are read-only on update. */
export interface UpdateIngestorRecordRequest {
  integration_credentials: IntegrationCredentials;
  is_multitenant?: boolean;
  configuration_status: number;
}

export interface CreateIngestorRecordResponse extends CreateIngestorRecordRequest {
  id: number;
  created_at: string;
  updated_at: string;
  elastic_sync_status?: ElasticSyncStatus;
  elastic_last_synced_at?: string | null;
  elastic_last_sync_error?: string | null;
}

export interface ConfigurationStatus {
  id: number;
  name: string;
}

export interface IngestorOrganization {
  id: number;
  name: string;
}

export interface IngestorIntegration {
  id: number;
  name: string;
  icon: string;
  category: {
    id: number;
    name: string;
  };
  description: string | null;
  display_name: string;
}

export interface IngestorTenantMapOrganization {
  id: number;
  name: string;
  parent_id: number | null;
}

/** One row of ingestor_integration_api_v1.IngestorTenantMap as the list endpoint serializes it. */
export interface IngestorTenantMap {
  id: number;
  integration_id: string;
  integration_unique_name: string | null;
  organization: IngestorTenantMapOrganization;
}

export interface IngestorRecordDetail {
  id: number;
  organization: IngestorOrganization;
  ingestor_integration: IngestorIntegration;
  tenant_mappings: IngestorTenantMap[];
  is_multitenant: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  configuration_status: ConfigurationStatus;
  elastic_sync_status?: ElasticSyncStatus;
  elastic_last_synced_at?: string | null;
  elastic_last_sync_error?: string | null;
}

export interface IngestorRecordDetailResponse {
  '@odata.count': number;
  value: IngestorRecordDetail[];
}
